import { PerspectiveCamera, Vector3 } from "three";
import type { HoleLayout } from "@shared/physics";

const UP = new Vector3(0, 1, 0);

// Upper caps on 3rd-person follow + shot-cam distances. Values chosen
// to match the daily-H9 feel (arena span ≈ 160 → backDist 40, height
// 19). Bigger arenas (late endless) stop zooming past these so the
// ball stays legible on-screen. Bird cam (telescope) is NOT capped —
// overview still fits the full arena.
const THIRD_PERSON_MAX_BACK = 40;
const THIRD_PERSON_MAX_HEIGHT = 20;
// How far ahead of the ball the 3rd-person camera aims. Was "look at
// the actual target" which on big arenas pushed the ball to the edge
// of the frame.
const THIRD_PERSON_LOOKAHEAD = 28;
const SHOT_CAM_MAX_DIST = 34;
const SHOT_CAM_MAX_HEIGHT = 28;

interface IntroState {
  startAt: number;
  durationSec: number;
}

interface ShotCamState {
  startAt: number;
  durationSec: number;
  // Current ball position in world space, updated externally each frame.
  ball: { x: number; z: number };
  // Direction of motion at launch (for camera-behind placement).
  dirX: number;
  dirZ: number;
}

export class HoleCamera {
  private target = new Vector3();
  private desired = new Vector3();
  private desiredTarget = new Vector3();
  private intro: IntroState | null = null;
  private shotCam: ShotCamState | null = null;
  private shakeStrength = 0;
  private shakeDecay = 6; // per second
  private birdCam: boolean = false;
  private birdCamTransition: number = 0;

  constructor(
    private camera: PerspectiveCamera,
    private hole: HoleLayout,
  ) {
    this.frameHole();
    camera.position.copy(this.desired);
    camera.up.copy(UP);
    camera.lookAt(this.target);
  }

  setBirdCam(active: boolean) {
    this.birdCam = active;
  }
  
  isBirdCam() {
    return this.birdCam;
  }

  getBirdCamTransition() {
    return this.birdCamTransition;
  }

  isIntro() {
    return this.intro !== null;
  }

  setHole(hole: HoleLayout, opts: { intro?: boolean } = {}): void {
    this.hole = hole;
    // Dynamically size the camera's far plane so bird cam can still
    // fit huge endless arenas (span * 1.2 is the overhead height).
    // Daily stays at the default 3000; endless scales up as needed.
    const span = Math.max(
      hole.bounds.maxX - hole.bounds.minX,
      hole.bounds.maxZ - hole.bounds.minZ,
    );
    const neededFar = Math.max(3000, span * 2.2);
    if (Math.abs(this.camera.far - neededFar) > 1) {
      this.camera.far = neededFar;
      this.camera.updateProjectionMatrix();
    }
    this.frameHole();
    if (opts.intro !== false) {
      // Start an intro sweep: come from above-behind the tee past the target.
      this.intro = { startAt: -1, durationSec: 2.2 };
    }
  }

  beginShotCam(ball: { x: number; z: number }, dir: { x: number; z: number }): void {
    // Don't chain new shot cams while one is active — keep the first to avoid snapping.
    if (this.shotCam) return;
    this.shotCam = {
      startAt: -1,
      durationSec: 0.9,
      ball: { x: ball.x, z: ball.z },
      dirX: dir.x,
      dirZ: dir.z,
    };
  }

  updateShotCamBall(x: number, z: number): void {
    if (this.shotCam) {
      this.shotCam.ball.x = x;
      this.shotCam.ball.z = z;
    }
  }

  endShotCam(): void {
    this.shotCam = null;
  }

  shake(amount: number): void {
    this.shakeStrength = Math.min(this.shakeStrength + amount, 1.5);
  }

  followBall(
    ballX: number,
    ballY: number,
    ballZ: number,
    blend = 0.08,
    t: number = 0,
  ): void {
    // ---- Intro sweep (highest priority, but short) ----
    if (this.intro) {
      if (this.intro.startAt < 0) this.intro.startAt = t;
      const elapsed = t - this.intro.startAt;
      const k = Math.min(1, elapsed / this.intro.durationSec);
      const eased = easeInOutCubic(k);
      this.applyIntroFrame(eased);
      this.applyShake(t);
      this.camera.lookAt(this.target);
      if (k >= 1) this.intro = null;
      return;
    }

    // ---- Shot cam: ride low and behind the ball ----
    if (this.shotCam) {
      if (this.shotCam.startAt < 0) this.shotCam.startAt = t;
      const elapsed = t - this.shotCam.startAt;
      const k = Math.min(1, elapsed / this.shotCam.durationSec);

      // Ease camera back toward a chase position; once done, we release below.
      const bx = this.shotCam.ball.x;
      const bz = this.shotCam.ball.z;
      const span = Math.max(
        this.hole.bounds.maxX - this.hole.bounds.minX,
        this.hole.bounds.maxZ - this.hole.bounds.minZ,
      );
      // Cap the follow distance + height so late endless holes (arena
      // span 400+) don't push the camera so far out the ball becomes
      // an invisible pixel. Caps chosen to match the daily-H9 feel:
      // above those spans we stop zooming out.
      const dist = Math.min(SHOT_CAM_MAX_DIST, span * 0.22);
      const dirLen = Math.hypot(this.shotCam.dirX, this.shotCam.dirZ) || 1;
      const bx2 = bx - (this.shotCam.dirX / dirLen) * dist;
      const bz2 = bz - (this.shotCam.dirZ / dirLen) * dist;
      const height = ballY + Math.min(SHOT_CAM_MAX_HEIGHT, span * 0.18);

      const desired = new Vector3(bx2, height, bz2);
      this.target.lerp(new Vector3(bx, ballY, bz), 0.2);
      // On first frame, snap — otherwise lerp for silky follow.
      if (elapsed < 1 / 60) {
        this.camera.position.copy(desired);
      } else {
        this.camera.position.lerp(desired, 0.12);
      }
      this.applyShake(t);
      this.camera.lookAt(this.target);

      if (k >= 1) this.shotCam = null;
      // Silence unused warning.
      void blend;
      return;
    }

    // ---- 3rd Person Golf Follow & Bird Cam Blend ----
    const tx = this.hole.target.cx;
    const ty = 0;
    const tz = this.hole.target.cz;

    let dx = tx - ballX;
    let dz = tz - ballZ;
    let dLen = Math.hypot(dx, dz);
    if (dLen < 0.1) { dx = 1; dz = 0; dLen = 1; }

    const dirX = dx / dLen;
    const dirZ = dz / dLen;

    const span = Math.max(
      this.hole.bounds.maxX - this.hole.bounds.minX,
      this.hole.bounds.maxZ - this.hole.bounds.minZ,
    );

    // 3rd person target — capped on the upper end so late-endless
    // arenas don't zoom the camera out past the ball's legibility
    // threshold. The caps land around daily-H9 numbers; bigger arenas
    // stop growing. Bird cam (telescope) stays uncapped so the
    // overview still fits the whole playfield.
    const backDist = Math.min(
      THIRD_PERSON_MAX_BACK,
      Math.max(12, span * 0.25),
    );
    const height3rd =
      ballY +
      Math.min(THIRD_PERSON_MAX_HEIGHT, Math.max(6, span * 0.12));
    const desired3rd = new Vector3(ballX - dirX * backDist, height3rd, ballZ - dirZ * backDist);
    // Look just ahead of the ball, not all the way to the target. On
    // huge endless arenas the target may be 4000+ units away, which
    // pushes the ball onto the edge of the frame when the camera
    // points at it. Clamping the look-ahead distance keeps the ball
    // roughly centered regardless of arena size.
    const lookAhead = Math.min(THIRD_PERSON_LOOKAHEAD, dLen);
    const target3rd = new Vector3(
      ballX + dirX * lookAhead,
      ty,
      ballZ + dirZ * lookAhead,
    );

    // Bird cam target
    const cx = (this.hole.bounds.minX + this.hole.bounds.maxX) / 2;
    const cz = (this.hole.bounds.minZ + this.hole.bounds.maxZ) / 2;
    const heightBird = span * 1.2;
    const desiredBird = new Vector3(cx, heightBird, cz + 2); // Slight offset to look slightly angled down
    const targetBird = new Vector3(cx, 0, cz);

    // Smooth transition
    const dt = 1 / 60; // approximate
    if (this.birdCam) {
      this.birdCamTransition = Math.min(1, this.birdCamTransition + dt * 2.5);
    } else {
      this.birdCamTransition = Math.max(0, this.birdCamTransition - dt * 2.5);
    }

    const tBlend = easeInOutCubic(this.birdCamTransition);
    this.desired.lerpVectors(desired3rd, desiredBird, tBlend);
    this.desiredTarget.lerpVectors(target3rd, targetBird, tBlend);

    this.camera.position.lerp(this.desired, blend);
    this.target.lerp(this.desiredTarget, blend);
    this.applyShake(t);
    this.camera.lookAt(this.target);
  }

  frameHole(): void {
    const cx = (this.hole.bounds.minX + this.hole.bounds.maxX) / 2;
    const cy = 0;
    const cz = (this.hole.bounds.minZ + this.hole.bounds.maxZ) / 2;
    this.target.set(cx, cy, cz);
    this.desiredTarget.copy(this.target);
    const offset = this.idealOffset(0);
    this.desired.set(cx + offset.x, cy + this.idealHeight(), cz + offset.z);
  }

  private applyIntroFrame(eased: number): void {
    const cx = (this.hole.bounds.minX + this.hole.bounds.maxX) / 2;
    const cy = 0;
    const cz = (this.hole.bounds.minZ + this.hole.bounds.maxZ) / 2;
    const span = Math.max(
      this.hole.bounds.maxX - this.hole.bounds.minX,
      this.hole.bounds.maxZ - this.hole.bounds.minZ,
    );

    // Start behind the tee looking toward target, end at standard orbit pose.
    const teeX = this.hole.tee.x;
    const teeY = 0;
    const teeZ = this.hole.tee.z;
    const targetX = this.hole.target.cx;
    const targetY = 0;
    const targetZ = this.hole.target.cz;
    const toTargetX = targetX - teeX;
    const toTargetZ = targetZ - teeZ;
    const dLen = Math.hypot(toTargetX, toTargetZ) || 1;

    // Start pose: camera is behind the tee, high, looking down the hole.
    const sx = teeX - (toTargetX / dLen) * span * 0.35;
    const sz = teeZ - (toTargetZ / dLen) * span * 0.35;
    const sy = teeY + span * 0.55;
    const stx = targetX;
    const sty = targetY;
    const stz = targetZ;

    // End pose: standard orbit.
    const endOffset = this.idealOffset(0);
    const ex = cx + endOffset.x;
    const ez = cz + endOffset.z;
    const ey = cy + this.idealHeight();

    this.camera.position.set(
      sx + (ex - sx) * eased,
      sy + (ey - sy) * eased,
      sz + (ez - sz) * eased,
    );
    this.target.set(
      stx + (cx - stx) * eased,
      sty + (cy - sty) * eased,
      stz + (cz - stz) * eased,
    );
  }

  private applyShake(t: number): void {
    if (this.shakeStrength <= 0) return;
    // Decay proportional to time. We approximate per-frame with a small dt.
    this.shakeStrength = Math.max(0, this.shakeStrength - this.shakeDecay * (1 / 60));
    const s = this.shakeStrength;
    const n1 = Math.sin(t * 63.7) * Math.cos(t * 31.2);
    const n2 = Math.sin(t * 81.1 + 2.1) * Math.cos(t * 17.3);
    this.camera.position.x += n1 * s * 0.25;
    this.camera.position.y += n2 * s * 0.18;
    this.camera.position.z += (n1 + n2) * 0.5 * s * 0.25;
  }

  private idealHeight(): number {
    const spanX = this.hole.bounds.maxX - this.hole.bounds.minX;
    const spanZ = this.hole.bounds.maxZ - this.hole.bounds.minZ;
    // Lower than before — was 0.9, now 0.42 for a cinematic low angle.
    return Math.max(spanX, spanZ) * 0.42;
  }

  private idealOffset(t: number): { x: number; z: number } {
    // Subtle slow orbit so static framing has life.
    const spanZ = this.hole.bounds.maxZ - this.hole.bounds.minZ;
    const spanX = this.hole.bounds.maxX - this.hole.bounds.minX;
    const r = Math.max(spanX, spanZ) * 0.75;
    const theta = Math.PI * 0.65 + Math.sin(t * 0.05) * 0.08;
    return { x: Math.cos(theta) * r, z: Math.sin(theta) * r };
  }
}

function easeInOutCubic(x: number): number {
  return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
}
